import type {
  ConnectedConnectorResponse,
  ConnectionStatus,
} from '~/features/onboarding/types/connection.types';

/**
 * Per-status presentation tokens for a connection's status badge + dot.
 * Colours use the project's semantic status tokens (success/warning/info/error)
 * so the page stays theme-aware. Labels are resolved via i18n at the call site,
 * not here.
 */
export const CONNECTION_STATUS_META: Record<
  ConnectionStatus,
  { dotClass: string; badgeClass: string }
> = {
  ACTIVE: { dotClass: 'bg-success', badgeClass: 'bg-success-50 text-success border-success/30' },
  PAUSED: { dotClass: 'bg-warning', badgeClass: 'bg-warning-50 text-warning border-warning/30' },
  PENDING: { dotClass: 'bg-info', badgeClass: 'bg-info-50 text-info border-info/30' },
  VALIDATING: { dotClass: 'bg-info', badgeClass: 'bg-info-50 text-info border-info/30' },
  INVALID: { dotClass: 'bg-error', badgeClass: 'bg-error-50 text-error border-error/30' },
  ERROR: { dotClass: 'bg-error', badgeClass: 'bg-error-50 text-error border-error/30' },
  REAUTH_REQUIRED: {
    dotClass: 'bg-warning',
    badgeClass: 'bg-warning-50 text-warning border-warning/30',
  },
  DISCONNECTED: {
    dotClass: 'bg-muted-foreground',
    badgeClass: 'bg-muted text-muted-foreground border-border',
  },
};

/** Turn a SNAKE_CASE connector code (e.g. `GOOGLE_MERCHANT`) into a human
 *  label (`Google Merchant`). Pure display helper — no i18n, the codes are
 *  brand names not translatable copy. */
export function humanizeConnectionType(type: string): string {
  return type
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** First two initials of a connector code, for the logo-fallback avatar when a
 *  connection's type has no `iconUrl`. */
export function getConnectionTypeInitials(type: string): string {
  const parts = type.split('_').filter(Boolean);
  if (parts.length >= 2) {
    const [first, second] = parts;
    return `${first?.[0] ?? ''}${second?.[0] ?? ''}`.toUpperCase();
  }
  return type.slice(0, 2).toUpperCase();
}

/** Index `type -> iconUrl` from the connected-connectors summary so connection
 *  rows (which don't carry a logo field) can look up their connector logo in
 *  O(1). Missing/absent icons resolve to `null` → the row renders the initials
 *  fallback. */
export function buildConnectorIconMap(
  connected: readonly ConnectedConnectorResponse[] | undefined,
): Map<string, string | null> {
  return new Map((connected ?? []).map((c) => [c.code, c.iconUrl ?? null]));
}
